import { motion } from "framer-motion";
import Reveal, { stagger, fadeUp } from "@/components/Reveal";
import { Check, Briefcase, Building, Target, Globe } from "lucide-react";

const ROLES = [
  {
    icon: Briefcase,
    role: "Business Owners & Founders",
    tagline: "Scale revenue while keeping operations lean",
    bullets: [
      "Offload manual prospecting & PA admin",
      "Unlock high-value partnerships",
      "Clear strategic growth roadmaps",
    ],
  },
  {
    icon: Building,
    role: "Real Estate Agency Principals",
    tagline: "Keep your appraisal pipeline full",
    bullets: [
      "Dedicated cold calling & follow-ups",
      "Faster vendor lead conversion",
      "Sharper listing presentations",
    ],
  },
  {
    icon: Target,
    role: "Sales Leaders & Directors",
    tagline: "Empower your team to close more deals",
    bullets: [
      "Targeted sales training & scripts",
      "Structured follow-up cadences",
      "Higher conversion at every touchpoint",
    ],
  },
  {
    icon: Globe,
    role: "Corporate Growth Executives",
    tagline: "Expand market share across NZ & AU",
    bullets: [
      "Direct intros to key decision-makers",
      "Multi-channel marketing campaigns",
      "Strategic partnership development",
    ],
  },
];

export default function WhoWeHelp() {
  return (
    <section id="who-we-help" data-testid="who-we-help-section" className="py-20 sm:py-28 bg-slate-50/70 border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
              One growth partner for every role
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              Tailored support for every leader looking to scale revenue without
              adding unnecessary overhead.
            </p>
          </Reveal>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {ROLES.map((r, i) => (
            <motion.div
              key={r.role}
              variants={fadeUp}
              data-testid={`who-we-help-card-${i}`}
              className="bg-white rounded-2xl border border-slate-200 p-7 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-600/5 hover:-translate-y-1 transition-all duration-300"
            >
              <span className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <r.icon className="w-5 h-5 text-emerald-600" />
              </span>
              <h3 className="font-display mt-5 text-lg font-bold text-slate-900 leading-snug">{r.role}</h3>
              <p className="mt-1.5 text-sm text-emerald-700 font-medium">{r.tagline}</p>
              <ul className="mt-5 space-y-2.5">
                {r.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
